<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  useStoreGetters,
  useStore,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const { accountId } = useAccount();
const { isFeatureFlagEnabled } = usePolicy();

const contactReasonsEnabled = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.CONTACT_REASONS)
);

const account = computed(() =>
  getters['accounts/getAccount'].value(accountId.value)
);
const allLabels = computed(() => getters['labels/getLabels'].value);
const allContactReasons = computed(
  () => getters['contactReasons/getContactReasons'].value || []
);

// ── Integration hook status ──────────────────────────────────────────────────
const openaiIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'openai'
);
const geminiIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'gemini'
);

const isIntegrationConfigured = integration =>
  integration.value?.hooks?.some(h => h.settings?.api_key);

const openaiConfigured = computed(() =>
  isIntegrationConfigured(openaiIntegration)
);
const geminiConfigured = computed(() =>
  isIntegrationConfigured(geminiIntegration)
);
const noAiConfigured = computed(
  () => !openaiConfigured.value && !geminiConfigured.value
);

const goToIntegrations = () =>
  router.push({
    name: 'settings_applications_integration',
    params: { accountId: accountId.value, integration_id: 'openai' },
  });

const AI_MODELS = {
  openai: [
    { id: 'gpt-4o', name: 'GPT-4o' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
    { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
    { id: 'gpt-4.1', name: 'GPT-4.1' },
    { id: 'gpt-4.1-mini', name: 'GPT-4.1 Mini' },
    { id: 'o3-mini', name: 'o3 Mini' },
  ],
  gemini: [
    { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
    { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
    { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash' },
    { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' },
  ],
};

const buildAiProvider = (id, configured, key) => ({
  id,
  label: configured
    ? t(`UNHAPPY_CASES.SETTINGS.AI.PROVIDERS.${key}`)
    : t(`UNHAPPY_CASES.SETTINGS.AI.PROVIDERS.${key}_NO_KEY`),
  disabled: !configured,
});

const aiProviders = computed(() => [
  buildAiProvider('openai', openaiConfigured.value, 'OPENAI'),
  buildAiProvider('gemini', geminiConfigured.value, 'GEMINI'),
]);

// ── Tab state ───────────────────────────────────────────────────────────────
const selectedTabIndex = ref(0);
const tabs = [
  { key: 'signals', name: t('UNHAPPY_CASES.SETTINGS.SIGNALS.TITLE') },
  { key: 'ai', name: t('UNHAPPY_CASES.SETTINGS.AI.TITLE') },
];

// ── Signal state ────────────────────────────────────────────────────────────
const lowCsatEnabled = ref(true);
const lowCsatThreshold = ref(2);
const reopenEnabled = ref(true);
const slackAlertedEnabled = ref(false);
const selectedLabelIds = ref([]);
const selectedContactReasonIds = ref([]);
const isSavingSignals = ref(false);

// ── AI state ────────────────────────────────────────────────────────────────
const aiProvider = ref('openai');
const aiModel = ref('gpt-4o');
const aiPrompt = ref('');
const isSavingAi = ref(false);

const availableModels = computed(() => AI_MODELS[aiProvider.value] || []);

// ── Contact reasons tree ─────────────────────────────────────────────────────
const flattenContactReasons = (reasons, depth = 0) => {
  const result = [];
  for (const r of reasons) {
    result.push({ id: r.id, name: r.name, depth });
    if (r.child_reasons?.length) {
      result.push(...flattenContactReasons(r.child_reasons, depth + 1));
    }
  }
  return result;
};
const flatContactReasons = computed(() =>
  flattenContactReasons(allContactReasons.value)
);

// ── Load / Save ──────────────────────────────────────────────────────────────
function loadFromAccount() {
  const config = account.value?.settings?.unhappy_case || {};
  const s = config.signals || {};
  lowCsatEnabled.value = s.low_csat?.enabled ?? true;
  lowCsatThreshold.value = s.low_csat?.threshold ?? 2;
  reopenEnabled.value = s.reopen?.enabled ?? true;
  slackAlertedEnabled.value = s.slack_alerted?.enabled ?? false;
  selectedLabelIds.value = s.label_ids || [];
  selectedContactReasonIds.value = s.contact_reason_ids || [];

  const ai = config.ai || {};
  aiProvider.value = ai.provider || 'openai';
  aiModel.value = ai.model || 'gpt-4o';
  aiPrompt.value = ai.prompt || '';
}

const buildPayload = () => ({
  unhappy_case: {
    signals: {
      low_csat: {
        enabled: lowCsatEnabled.value,
        threshold: lowCsatThreshold.value,
      },
      reopen: { enabled: reopenEnabled.value },
      slack_alerted: { enabled: slackAlertedEnabled.value },
      label_ids: selectedLabelIds.value,
      contact_reason_ids: selectedContactReasonIds.value,
    },
    ai: {
      provider: aiProvider.value,
      model: aiModel.value,
      prompt: aiPrompt.value || null,
    },
  },
});

const save = async isSaving => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', buildPayload());
    useAlert(t('UNHAPPY_CASES.SETTINGS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('UNHAPPY_CASES.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveSignals = () => save(isSavingSignals);
const saveAi = () => save(isSavingAi);

const onProviderChange = () => {
  const models = AI_MODELS[aiProvider.value] || [];
  aiModel.value = models[0]?.id || '';
};

const toggleArrayItem = (arr, id) => {
  const idx = arr.value.indexOf(id);
  if (idx === -1) arr.value.push(id);
  else arr.value.splice(idx, 1);
};

const toggleLabel = id => toggleArrayItem(selectedLabelIds, id);
const toggleContactReason = id => toggleArrayItem(selectedContactReasonIds, id);

onBeforeMount(() => {
  store.dispatch('integrations/get');
  store.dispatch('labels/get');
  if (contactReasonsEnabled.value) store.dispatch('contactReasons/get');
  loadFromAccount();
});
</script>

<template>
  <div class="flex flex-col w-full">
    <BaseSettingsHeader
      :title="$t('UNHAPPY_CASES.SETTINGS.TITLE')"
      :description="$t('UNHAPPY_CASES.SETTINGS.DESCRIPTION')"
    />

    <!-- No AI key warning -->
    <div
      v-if="noAiConfigured"
      class="flex items-start gap-3 rounded-xl border border-n-amber-6 bg-n-amber-3 px-4 py-3 text-n-amber-11"
    >
      <fluent-icon icon="warning" size="16" class="mt-0.5 flex-shrink-0" />
      <p class="flex-1 text-sm">
        {{ $t('UNHAPPY_CASES.SETTINGS.AI.NO_KEY_WARNING') }}
        <button
          type="button"
          class="ml-1 font-medium underline hover:no-underline"
          @click="goToIntegrations"
        >
          {{ $t('UNHAPPY_CASES.SETTINGS.AI.SETUP_INTEGRATIONS') }}
        </button>
      </p>
    </div>

    <!-- Tabs -->
    <template v-if="!noAiConfigured">
      <div class="mt-4">
        <woot-tabs
          :index="selectedTabIndex"
          @change="selectedTabIndex = $event"
        >
          <woot-tabs-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :show-badge="false"
            is-compact
          />
        </woot-tabs>
      </div>

      <section class="mx-auto w-full max-w-6xl">
        <!-- ── Tab: Detection Signals ─────────────────────────── -->
        <div v-if="selectedTabIndex === 0" class="mx-8">
          <form @submit.prevent="saveSignals">
            <SettingsSection
              :title="$t('UNHAPPY_CASES.SETTINGS.SIGNALS.TITLE')"
              :sub-title="$t('UNHAPPY_CASES.SETTINGS.DESCRIPTION')"
            >
              <div
                class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
              >
                <!-- Low CSAT -->
                <div class="p-3 flex flex-col gap-2">
                  <div class="flex items-center justify-between h-6">
                    <span>{{
                      $t('UNHAPPY_CASES.SETTINGS.SIGNALS.LOW_CSAT.LABEL')
                    }}</span>
                    <Switch v-model="lowCsatEnabled" />
                  </div>
                  <div v-if="lowCsatEnabled" class="flex items-center gap-2">
                    <span class="text-xs text-n-slate-10 flex-1">
                      {{
                        $t(
                          'UNHAPPY_CASES.SETTINGS.SIGNALS.LOW_CSAT.THRESHOLD_LABEL'
                        )
                      }}
                    </span>
                    <select
                      v-model="lowCsatThreshold"
                      class="w-[100px] border border-n-weak rounded px-2 py-1 text-sm bg-n-alpha-2 text-n-slate-12"
                    >
                      <option v-for="n in 4" :key="n" :value="n">
                        {{ n }}
                      </option>
                    </select>
                  </div>
                </div>
                <!-- Reopen -->
                <div class="p-3 h-12 flex items-center justify-between">
                  <span>{{
                    $t('UNHAPPY_CASES.SETTINGS.SIGNALS.REOPEN.LABEL')
                  }}</span>
                  <Switch v-model="reopenEnabled" />
                </div>
                <!-- Slack Alerted -->
                <div class="p-3 h-12 flex items-center justify-between">
                  <span>{{
                    $t('UNHAPPY_CASES.SETTINGS.SIGNALS.SLACK_ALERTED.LABEL')
                  }}</span>
                  <Switch v-model="slackAlertedEnabled" />
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              :title="$t('UNHAPPY_CASES.SETTINGS.SIGNALS.LABELS.LABEL')"
              :sub-title="
                $t('UNHAPPY_CASES.SETTINGS.SIGNALS.LABELS.DESCRIPTION')
              "
            >
              <div class="flex flex-wrap gap-2 w-full">
                <button
                  v-for="label in allLabels"
                  :key="label.id"
                  type="button"
                  class="px-3 py-1 rounded-full text-sm border transition-colors"
                  :class="
                    selectedLabelIds.includes(label.id)
                      ? 'border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-800 dark:text-woot-200'
                      : 'border-n-weak text-n-slate-11 hover:border-n-slate-7'
                  "
                  @click="toggleLabel(label.id)"
                >
                  {{ label.title }}
                </button>
                <span v-if="!allLabels.length" class="text-sm text-n-slate-9">
                  {{ $t('UNHAPPY_CASES.SETTINGS.SIGNALS.LABELS.PLACEHOLDER') }}
                </span>
              </div>
            </SettingsSection>

            <SettingsSection
              v-if="contactReasonsEnabled"
              :title="
                $t('UNHAPPY_CASES.SETTINGS.SIGNALS.CONTACT_REASONS.LABEL')
              "
              :sub-title="
                $t('UNHAPPY_CASES.SETTINGS.SIGNALS.CONTACT_REASONS.DESCRIPTION')
              "
              :show-border="false"
            >
              <div
                class="flex flex-col w-full rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
              >
                <label
                  v-for="reason in flatContactReasons"
                  :key="reason.id"
                  class="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-n-alpha-1 text-sm text-n-slate-12"
                  :style="{ paddingLeft: `${12 + reason.depth * 20}px` }"
                >
                  <input
                    type="checkbox"
                    class="w-4 h-4 rounded accent-woot-500 flex-shrink-0"
                    :checked="selectedContactReasonIds.includes(reason.id)"
                    @change="toggleContactReason(reason.id)"
                  />
                  <span
                    :class="
                      reason.depth > 0 ? 'text-n-slate-11' : 'font-medium'
                    "
                  >
                    {{ reason.name }}
                  </span>
                </label>
                <p
                  v-if="!flatContactReasons.length"
                  class="px-3 py-2.5 text-sm text-n-slate-9"
                >
                  {{
                    $t(
                      'UNHAPPY_CASES.SETTINGS.SIGNALS.CONTACT_REASONS.PLACEHOLDER'
                    )
                  }}
                </p>
              </div>
            </SettingsSection>

            <div class="ml-0 mr-0 py-8 w-full">
              <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
                <div class="col-span-2" />
                <div class="col-span-6 xl:col-span-5">
                  <NextButton blue type="submit" :is-loading="isSavingSignals">
                    {{ $t('UNHAPPY_CASES.SETTINGS.SAVE') }}
                  </NextButton>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- ── Tab: AI Analysis ───────────────────────────────── -->
        <div v-if="selectedTabIndex === 1" class="mx-8">
          <form @submit.prevent="saveAi">
            <SettingsSection
              :title="$t('UNHAPPY_CASES.SETTINGS.AI.MODEL_LABEL')"
              :sub-title="$t('UNHAPPY_CASES.SETTINGS.AI.MODEL_DESCRIPTION')"
            >
              <div class="flex gap-3 w-full">
                <select
                  v-model="aiProvider"
                  class="border border-n-weak rounded px-3 py-2 text-sm bg-n-alpha-2 text-n-slate-12 flex-1"
                  @change="onProviderChange"
                >
                  <option
                    v-for="provider in aiProviders"
                    :key="provider.id"
                    :value="provider.id"
                    :disabled="provider.disabled"
                  >
                    {{ provider.label }}
                  </option>
                </select>
                <select
                  v-model="aiModel"
                  class="border border-n-weak rounded px-3 py-2 text-sm bg-n-alpha-2 text-n-slate-12 flex-1"
                >
                  <option
                    v-for="m in availableModels"
                    :key="m.id"
                    :value="m.id"
                  >
                    {{ m.name }}
                  </option>
                </select>
              </div>
            </SettingsSection>

            <SettingsSection
              :title="$t('UNHAPPY_CASES.SETTINGS.AI.PROMPT_LABEL')"
              :sub-title="$t('UNHAPPY_CASES.SETTINGS.AI.PROMPT_DESCRIPTION')"
              :show-border="false"
            >
              <div class="flex flex-col gap-2 w-full">
                <div class="flex justify-end">
                  <button
                    type="button"
                    class="text-xs text-woot-500 hover:text-woot-600 font-medium"
                    @click="aiPrompt = ''"
                  >
                    {{ $t('UNHAPPY_CASES.SETTINGS.AI.RESET_PROMPT') }}
                  </button>
                </div>
                <textarea
                  v-model="aiPrompt"
                  class="h-[20rem] w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-alpha-2 text-n-slate-12 font-mono resize-y focus:outline-none focus:ring-1 focus:ring-woot-500"
                  :placeholder="
                    $t('UNHAPPY_CASES.SETTINGS.AI.PROMPT_PLACEHOLDER')
                  "
                />
              </div>
            </SettingsSection>

            <div class="ml-0 mr-0 py-8 w-full">
              <div class="grid grid-cols-1 lg:grid-cols-8 gap-6">
                <div class="col-span-2" />
                <div class="col-span-6 xl:col-span-5">
                  <NextButton blue type="submit" :is-loading="isSavingAi">
                    {{ $t('UNHAPPY_CASES.SETTINGS.SAVE') }}
                  </NextButton>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </template>
  </div>
</template>
